import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Download } from 'lucide-react';
import ReportHeader from '@/components/ReportHeader';
import { apiService } from '@/services/api';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
`;

const ScrollTop = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  margin: 0 16px 8px 16px;
`;

const ScrollBottom = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

const RelatorioComissoes: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const [dataInicio, setDataInicio] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [todasFiliais, setTodasFiliais] = useState<boolean>(true);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const isValidDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
  const datasValidas = isValidDate(dataInicio) && isValidDate(dataFim);

  const queryKey = useMemo(() => (
    ['relatorio-comissoes', todasFiliais ? 'todas' : (filialSelecionada?.id || '-'), dataInicio, dataFim]
  ), [todasFiliais, filialSelecionada?.id, dataInicio, dataFim]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey,
    enabled: datasValidas && (todasFiliais || !!filialSelecionada?.id),
    queryFn: async () => {
      if (todasFiliais) return apiService.obterComissoesMecanicosTodas({ dataInicio, dataFim });
      if (!filialSelecionada?.id) return null;
      return apiService.obterComissoesMecanicosFilial(filialSelecionada.id, { dataInicio, dataFim });
    }
  });

  const colunas: string[] = data?.data?.colunas || [
    'Filial','NomeTecnico','TotalQuantidades','ConsertoDeRoda','ServicosAlinhamentoTecnico','Pecas',
    'ValorUnitRoda','ValorUnitAlinhamento','ValorUnitPecas',
    'ValorConsertoRoda','ValorAlinhamentoTecnico','ValorPecas','ComissaoTotal'
  ];
  const linhas: any[] = data?.data?.dados || [];

  const headerLabels = useMemo<Record<string, string>>(() => ({
    NomeTecnico: 'Nome do Técnico',
    TotalQuantidades: 'Total de Quantidades',
    ConsertoDeRoda: 'Conserto de Roda',
    ServicosAlinhamentoTecnico: 'Serviços Alinh. Técnico',
    ValorUnitRoda: 'Valor Unit. Roda (R$)',
    ValorUnitAlinhamento: 'Valor Unit. Alinh. Técnico (R$)',
    ValorUnitPecas: 'Valor Unit. Peças (R$)',
    ValorConsertoRoda: 'Valor Roda (R$)',
    ValorAlinhamentoTecnico: 'Valor Alinh. Técnico (R$)',
    ValorPecas: 'Valor Peças (R$)',
    ComissaoTotal: 'Comissão Total (R$)'
  }), []);

  const colunasValor = useMemo(() => new Set([
    'ValorUnitRoda','ValorUnitAlinhamento','ValorUnitPecas',
    'ValorConsertoRoda','ValorAlinhamentoTecnico','ValorPecas','ComissaoTotal'
  ]), []);

  const formatarNumero2Casas = (valor: any): string => {
    const num = typeof valor === 'number' ? valor : parseFloat(String(valor).replace(',', '.'));
    if (isNaN(num)) return String(valor ?? '');
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  useEffect(() => {
    const bottom = bottomScrollRef.current;
    if (!bottom) return;
    const update = () => setContentWidth(bottom.scrollWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [linhas, colunas]);

  const handleTopScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && bottom.scrollLeft !== top.scrollLeft) {
      bottom.scrollLeft = top.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && top.scrollLeft !== bottom.scrollLeft) {
      top.scrollLeft = bottom.scrollLeft;
    }
  };

  const handleExportar = () => {
    const rows = linhas || [];
    const cols = colunas || [];
    if (rows.length === 0 || cols.length === 0) return;
    const sep = ';';

    const toCsvValue = (coluna: string, valor: any): string => {
      const raw = colunasValor.has(coluna)
        ? formatarNumero2Casas(valor)
        : String(valor ?? '');
      const needsQuote = raw.includes('"') || raw.includes(sep) || /[\n\r]/.test(raw);
      const escaped = raw.replace(/"/g, '""');
      return needsQuote ? `"${escaped}"` : escaped;
    };

    const header = cols.join(sep);
    const body = rows.map((linha) => cols.map((c) => toCsvValue(c, linha[c])).join(sep)).join('\n');

    const prefixo = 'relatorio_comissoes';
    const contexto = todasFiliais ? 'todas' : (filialSelecionada?.nome?.replace(/[^\w\-\s]/g, '') || 'filial');
    const nomeArquivo = `${prefixo}_${contexto}_${dataInicio}_a_${dataFim}.csv`;

    const csvContent = `\ufeff${header}\n${body}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Container>
      <ReportHeader
        title="Relatório de Comissões por Técnico (todas as filiais)"
        subtitle={`${data?.data?.totalRegistros ?? 0} técnicos no período`}
        rightActions={(
          <Acoes>
            <Botao onClick={handleExportar} disabled={isLoading || isFetching || (linhas?.length ?? 0) === 0}>
              <Download size={16} /> Exportar
            </Botao>
            <Botao onClick={async () => {
              if (!datasValidas) return;
              await refetch();
            }} disabled={isLoading || isFetching}>
              <Calendar size={16} /> Aplicar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo>
          <Label>Data Início</Label>
          <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Data Fim</Label>
          <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={todasFiliais}
              onChange={(e) => setTodasFiliais(e.target.checked)}
            />
            Todas as Filiais
          </CheckboxRow>
        </Campo>
      </Filtros>

      <ScrollTop ref={topScrollRef} onScroll={handleTopScroll}>
        <div style={{ width: contentWidth }} />
      </ScrollTop>

      <ScrollBottom ref={bottomScrollRef} onScroll={handleBottomScroll}>
        <Tabela>
          <thead>
            <tr>
              {colunas.map((c) => (
                <Th key={c}>{headerLabels[c] ?? c}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><Td colSpan={colunas.length}>Carregando...</Td></tr>
            ) : (linhas?.length ?? 0) === 0 ? (
              <tr><Td colSpan={colunas.length}>Nenhum registro encontrado</Td></tr>
            ) : (
              linhas.map((linha, idx) => (
                <tr key={idx}>
                  {colunas.map((c) => (
                    <Td key={c}>
                      {colunasValor.has(c) ? formatarNumero2Casas(linha[c]) : String(linha[c] ?? '')}
                    </Td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Tabela>
      </ScrollBottom>
    </Container>
  );
};

export default RelatorioComissoes;


